import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import intakeAPI from '../../api/intake';
import { getApiErrorMessage } from '../../utils/errorUtils';
import {
  UI_TEXT,
  getOptionLabel,
  getQuestionLabel,
  getSectionTitle,
  getStepDescription,
  getStepTitle,
} from './intakeTranslations';

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return value === false;
  return false;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  return [value];
};

const PublicIntakeWizard = () => {
  const { token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [schema, setSchema] = useState(null);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('draft');
  const [answers, setAnswers] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [stepErrors, setStepErrors] = useState([]);
  const [missingFieldIds, setMissingFieldIds] = useState([]);
  const [language, setLanguage] = useState('');
  const initializedRef = useRef(false);

  const steps = useMemo(() => schema?.steps || [], [schema]);
  const totalSteps = steps.length || 1;
  const progress = ((activeStep + 1) / totalSteps) * 100;
  const ui = UI_TEXT[language || 'ru'];

  const isVisible = (question) => {
    if (!question?.showIf) return true;
    const rule = question.showIf;
    const value = answers[rule.id];

    if (rule.equals !== undefined) return value === rule.equals;
    if (rule.notEquals !== undefined) return value !== rule.notEquals;
    if (rule.in) return rule.in.includes(value);
    if (rule.notIncludes !== undefined) {
      return !toArray(value).includes(rule.notIncludes);
    }
    return true;
  };

  const getStepQuestions = (step) => {
    if (!step?.sections) return [];
    return step.sections.flatMap((section) => section.questions || []).filter(isVisible);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await intakeAPI.getPublicByToken(token);
        setStatus(data?.status || 'draft');
        setTitle(data?.title || 'Анкета');
        setSchema(data?.schema || null);
        setAnswers(data?.answers || {});
        setCompleted(data?.status !== 'draft');
        const storedLanguage = localStorage.getItem(`intake-lang-${token}`);
        if (storedLanguage === 'ru' || storedLanguage === 'en') {
          setLanguage(storedLanguage);
        }
        initializedRef.current = true;
      } catch (err) {
        setError(getApiErrorMessage(err, UI_TEXT[language || 'ru'].loadError));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    if (!initializedRef.current || status !== 'draft') return;
    const timeout = setTimeout(async () => {
      try {
        setSaving(true);
        const result = await intakeAPI.saveDraft(token, answers);
        setSavedAt(result?.savedAt || new Date().toISOString());
      } catch (err) {
        // Quiet autosave failures to avoid noisy UX; user still sees submit errors.
      } finally {
        setSaving(false);
      }
    }, 600);

    return () => clearTimeout(timeout);
  }, [answers, token, status]);

  const handleAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setMissingFieldIds((prev) => prev.filter((fieldId) => fieldId !== id));
  };

  const validateStep = () => {
    const current = steps[activeStep];
    const visibleQuestions = getStepQuestions(current);
    const missing = visibleQuestions.filter((q) => q.required && isEmptyValue(answers[q.id]));
    setMissingFieldIds(missing.map((q) => q.id));
    setStepErrors(missing.map((q) => getQuestionLabel(q, language || 'ru') || q.id));
    return missing.length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStepErrors([]);
    setMissingFieldIds([]);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    try {
      setSubmitting(true);
      await intakeAPI.submit(token, answers);
      setStatus('submitted');
      setCompleted(true);
      toast.success(ui.submitSuccess);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err, ui.submitError);
      const apiErrors = err?.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setStepErrors(apiErrors);
      }
      toast.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    if (!isVisible(question)) return null;

    const value = answers[question.id];
    const questionLabel = getQuestionLabel(question, language || 'ru');
    const hasError = missingFieldIds.includes(question.id);
    const fullNameHint = question.id === 'fullName' ? ui.fullNameEnglishHint : '';
    const commonProps = {
      fullWidth: true,
      required: Boolean(question.required),
      disabled: completed || status !== 'draft',
      margin: 'normal',
      size: 'small',
    };

    if (question.type === 'text' || question.type === 'date' || question.type === 'number') {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: hasError ? 'error.main' : 'text.primary' }}>
            {questionLabel}
          </Typography>
          {fullNameHint ? (
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: hasError ? 'error.main' : 'text.secondary' }}>
              {fullNameHint}
            </Typography>
          ) : null}
          <TextField
            {...commonProps}
            label={ui.answerLabel}
            placeholder={question.type === 'date' ? undefined : ui.typeAnswerPlaceholder}
            InputLabelProps={{ shrink: true }}
            type={question.type}
            value={value ?? ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            error={hasError}
            helperText={hasError ? ui.requiredFieldError : ''}
          />
        </Box>
      );
    }

    if (question.type === 'textarea') {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: hasError ? 'error.main' : 'text.primary' }}>
            {questionLabel}
          </Typography>
          {fullNameHint ? (
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: hasError ? 'error.main' : 'text.secondary' }}>
              {fullNameHint}
            </Typography>
          ) : null}
          <TextField
            {...commonProps}
            label={ui.answerLabel}
            placeholder={ui.typeAnswerPlaceholder}
            InputLabelProps={{ shrink: true }}
            multiline
            minRows={3}
            value={value ?? ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            error={hasError}
            helperText={hasError ? ui.requiredFieldError : ''}
          />
        </Box>
      );
    }

    if (question.type === 'yesno') {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: hasError ? 'error.main' : 'text.primary' }}>
            {questionLabel}
          </Typography>
          <FormControl {...commonProps} error={hasError}>
            <Select
              value={value ?? ''}
              displayEmpty
              onChange={(e) => handleAnswer(question.id, e.target.value)}
            >
              <MenuItem value="">
                <em>{ui.answerLabel}</em>
              </MenuItem>
              {(question.options || ['Да', 'Нет']).map((option) => (
                <MenuItem key={option} value={option}>
                  {getOptionLabel(option, language || 'ru')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasError ? (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {ui.requiredFieldError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (question.type === 'select') {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: hasError ? 'error.main' : 'text.primary' }}>
            {questionLabel}
          </Typography>
          <FormControl {...commonProps} error={hasError}>
            <Select
              value={value ?? ''}
              displayEmpty
              onChange={(e) => handleAnswer(question.id, e.target.value)}
            >
              <MenuItem value="">
                <em>{ui.answerLabel}</em>
              </MenuItem>
              {(question.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {getOptionLabel(option, language || 'ru')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasError ? (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {ui.requiredFieldError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (question.type === 'multiselect') {
      const values = Array.isArray(value) ? value : [];
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: hasError ? 'error.main' : 'text.primary' }}>
            {questionLabel}
          </Typography>
          <FormControl {...commonProps} error={hasError}>
            <Select
              multiple
              value={values}
              displayEmpty
              onChange={(e) => handleAnswer(question.id, e.target.value)}
              renderValue={(selected) => {
                if (!selected.length) return ui.answerLabel;
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '100%', overflow: 'hidden' }}>
                    {selected.map((v) => (
                      <Chip
                        key={v}
                        label={getOptionLabel(v, language || 'ru')}
                        size="small"
                        sx={{
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    ))}
                  </Box>
                );
              }}
            >
              {(question.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={values.includes(option)} />
                  <ListItemText primary={getOptionLabel(option, language || 'ru')} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasError ? (
            <Typography variant="caption" sx={{ color: 'error.main' }}>
              {ui.requiredFieldError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (question.type === 'checkbox') {
      return (
        <Box sx={{ mt: 2, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleAnswer(question.id, e.target.checked)}
                disabled={completed || status !== 'draft'}
                color={hasError ? 'error' : 'primary'}
              />
            }
            label={
              <Typography sx={{ color: hasError ? 'error.main' : 'text.primary' }}>
                {getQuestionLabel(question, language || 'ru')}
              </Typography>
            }
          />
          {hasError ? (
            <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>
              {ui.requiredFieldError}
            </Typography>
          ) : null}
        </Box>
      );
    }

    if (question.type === 'scale') {
      const min = Number(question.min || 1);
      const max = Number(question.max || 10);
      const numericValue = Number(value || min);
      return (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {getQuestionLabel(question, language || 'ru')}
          </Typography>
          <Slider
            value={numericValue}
            min={min}
            max={max}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_, newValue) => handleAnswer(question.id, newValue)}
            disabled={completed || status !== 'draft'}
          />
        </Box>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!schema) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">{ui.schemaUnavailable}</Alert>
      </Container>
    );
  }

  if (!language) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 8 }, px: { xs: 1.5, sm: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {UI_TEXT.ru.chooseLanguageTitle} / {UI_TEXT.en.chooseLanguageTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {UI_TEXT.ru.chooseLanguageSubtitle}
            <br />
            {UI_TEXT.en.chooseLanguageSubtitle}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              fullWidth={isMobile}
              onClick={() => {
                setLanguage('ru');
                localStorage.setItem(`intake-lang-${token}`, 'ru');
              }}
            >
              {UI_TEXT.ru.continueRu}
            </Button>
            <Button
              variant="outlined"
              fullWidth={isMobile}
              onClick={() => {
                setLanguage('en');
                localStorage.setItem(`intake-lang-${token}`, 'en');
              }}
            >
              {UI_TEXT.en.continueEn}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  const currentStep = steps[activeStep] || { sections: [] };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 4 }, borderRadius: 2 }}>
        <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold" gutterBottom sx={{ wordBreak: 'break-word' }}>
          {title}
        </Typography>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {ui.stepOf(Math.min(activeStep + 1, totalSteps), totalSteps)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              size="small"
              onClick={() =>
                setLanguage((prev) => {
                  const next = prev === 'ru' ? 'en' : 'ru';
                  localStorage.setItem(`intake-lang-${token}`, next);
                  return next;
                })
              }
            >
              {ui.changeLanguage}
            </Button>
            <Typography variant="body2" color={saving ? 'warning.main' : 'success.main'}>
              {saving ? ui.saving : savedAt ? ui.saved : ''}
            </Typography>
          </Box>
        </Box>

        <LinearProgress variant="determinate" value={progress} sx={{ mb: 2, height: 8, borderRadius: 8 }} />

        {isMobile ? (
          <Box sx={{ mb: 2 }}>
            <Chip
              size="small"
              color="primary"
              label={ui.stepOf(Math.min(activeStep + 1, totalSteps), totalSteps)}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
              {getStepTitle(currentStep, language)}
            </Typography>
          </Box>
        ) : (
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((step) => (
              <Step key={step.id}>
                <StepLabel>{getStepTitle(step, language)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {completed && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {ui.submittedBanner}
          </Alert>
        )}

        {stepErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
              {ui.requiredBlockTitle}
            </Typography>
            {stepErrors.map((item) => (
              <Typography key={item} variant="body2">
                • {item}
              </Typography>
            ))}
          </Alert>
        )}

        <Typography variant="h6" gutterBottom>
          {getStepTitle(currentStep, language)}
        </Typography>
        {getStepDescription(currentStep, language) && (
          <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {getStepDescription(currentStep, language)}
          </Alert>
        )}

        {currentStep.sections.map((section) => (
          <Box key={section.id} sx={{ mb: 3 }}>
            {getSectionTitle(section, language) && (
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {getSectionTitle(section, language)}
              </Typography>
            )}
            <Grid container spacing={2}>
              {(section.questions || [])
                .filter(isVisible)
                .map((question) => (
                  <Grid item xs={12} key={question.id}>
                    {renderQuestion(question)}
                  </Grid>
                ))}
            </Grid>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
          <Button variant="outlined" onClick={handleBack} disabled={activeStep === 0} fullWidth={isMobile}>
            {ui.back}
          </Button>
          {activeStep < totalSteps - 1 ? (
            <Button variant="contained" onClick={handleNext} fullWidth={isMobile}>
              {ui.next}
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleSubmit} disabled={submitting || completed || status !== 'draft'} fullWidth={isMobile}>
              {submitting ? ui.submitting : ui.submit}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PublicIntakeWizard;
